import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';

import selectArrowSvg from 'assets/images/select-arrow.svg';
import Button from 'components/form/Button.component';
import { TogglerInput } from 'components/form/TogglerInput.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import { MobileBottomSheet } from 'components/generic/MobileBottomSheet.component';
import LINK from 'constants/link.const';
import useIsMobileViewport from 'hooks/useIsMobileViewport.hook';
import useTip from 'hooks/useTip.hook';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import linkTo from 'utils/links/linkTo.util';

import { duplicateConsultationForm, feConsultationFormDelete, feConsultationFormToggleStatus } from 'settings/consultation-forms/consultationForms.action';
import { FORM_STATUS } from 'settings/consultation-forms/consultationForms.const';
import { ConfirmDeleteFormModal } from 'settings/consultation-forms/modals/ConfirmDeleteFormModal.component';
import { DeactivateFormModal } from 'settings/consultation-forms/modals/DeactivateFormModal.component';
import { ReactivateFormModal } from 'settings/consultation-forms/modals/ReactivateFormModal.component';

import CN from './FormRowManageDropdown.module.scss';

const DropdownItem = ({ icon, label, onClick, isRedText = false, badge, showArrow = false, isRedCircle = false, trailing, isMobile = false }) => (
  <button type="button" className={CN.dropdownItem} onClick={onClick}>
    {isMobile ? <div className={cn(CN.iconCircle, { [CN.iconCircleRed]: isRedCircle })}>{icon}</div> : icon}
    <div className={CN.dropdownItemMain}>
      <span
        className={cn({
          [CN.dropdownItemText]: true,
          [CN.redText]: isRedText && !isMobile,
        })}
      >
        {label}
      </span>
      {badge}
    </div>
    {trailing}
    {showArrow && <img src={selectArrowSvg} className={CN.arrowIcon} alt="" aria-hidden="true" />}
  </button>
);

export const FormRowManageDropdown = ({ form }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();
  const ref = useRef(null);

  const isMobile = useIsMobileViewport();
  const { val: isDropdownOpen, tip: toggleDropdown } = useTip(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottomRight');
  const { val: isDeleteModalOpen, yay: openDeleteModal, nay: closeDeleteModal } = useTip(false);
  const { val: isDeactivateOpen, yay: openDeactivate, nay: closeDeactivate } = useTip(false);
  const { val: isReactivateOpen, yay: openReactivate, nay: closeReactivate } = useTip(false);

  const { id: formId, name: formName, status: formStatus, ruleTags } = form;
  const isDraft = formStatus === FORM_STATUS.DRAFT;
  const isActive = formStatus === FORM_STATUS.ACTIVE;

  const handleToggleDropdown = useCallback(() => {
    if (!isDropdownOpen && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropdownPosition(spaceBelow < 250 ? 'topRight' : 'bottomRight');
    }
    toggleDropdown();
  }, [isDropdownOpen, toggleDropdown]);

  const handleEdit = useCallback(() => {
    const path = linkTo({
      pattern: LINK.settingsFormBuilder,
      params: { salonId, formId },
    });
    history.push(path);
    toggleDropdown();
  }, [history, salonId, formId, toggleDropdown]);

  const handleDuplicate = useCallback(() => {
    dispatch(duplicateConsultationForm(formId));
    toggleDropdown();
  }, [dispatch, formId, toggleDropdown]);

  const handleDeleteClick = useCallback(() => {
    openDeleteModal();
    toggleDropdown();
  }, [openDeleteModal, toggleDropdown]);

  const handleConfirmDelete = useCallback(() => {
    dispatch(feConsultationFormDelete(formId));
    closeDeleteModal();
  }, [dispatch, formId, closeDeleteModal]);

  const handleDeactivateFromDeleteModal = useCallback(() => {
    dispatch(feConsultationFormToggleStatus({ formId, newStatus: FORM_STATUS.INACTIVE }));
    closeDeleteModal();
  }, [dispatch, formId, closeDeleteModal]);

  const handleToggleChange = useCallback(() => {
    if (isActive) {
      openDeactivate();
    } else {
      openReactivate();
    }
    toggleDropdown();
  }, [isActive, openDeactivate, openReactivate, toggleDropdown]);

  const handleDeactivateConfirm = useCallback(() => {
    dispatch(feConsultationFormToggleStatus({ formId, newStatus: FORM_STATUS.INACTIVE }));
    closeDeactivate();
  }, [dispatch, formId, closeDeactivate]);

  const handleReactivateConfirm = useCallback(() => {
    dispatch(feConsultationFormToggleStatus({ formId, newStatus: FORM_STATUS.ACTIVE }));
    closeReactivate();
  }, [dispatch, formId, closeReactivate]);

  const renderedContent = (
    <div
      className={cn({
        [CN.dropdownContent]: true,
        [CN.mobile]: isMobile,
        [CN.darkContent]: isDarkMode,
      })}
    >
      {isDraft && (
        <DropdownItem
          isMobile={isMobile}
          icon={<Icon variant="clockCircleEmpty" width="lg" isCentered />}
          label={t('consultationForms.manageDropdown.setRules')}
          onClick={toggleDropdown}
          badge={<span className={CN.incompleteBadge}>{t('consultationForms.manageDropdown.incomplete')}</span>}
          showArrow={isMobile}
        />
      )}
      <DropdownItem
        isMobile={isMobile}
        icon={<Icon variant="eyePreview" width="lg" isCentered />}
        label={t('consultationForms.manageDropdown.preview')}
        onClick={toggleDropdown}
        showArrow={isMobile}
      />
      {isMobile && !isDraft && (
        <DropdownItem
          isMobile={isMobile}
          icon={<Icon variant={isActive ? 'checkCircleBlackOutline' : 'xCircleBlack'} width="lg" isCentered />}
          label={isActive ? t('consultationForms.manageDropdown.enabled') : t('consultationForms.manageDropdown.disabled')}
          onClick={handleToggleChange}
          trailing={<TogglerInput checked={isActive} onChange={handleToggleChange} className={CN.toggleInput} />}
        />
      )}
      <DropdownItem isMobile={isMobile} icon={<Icon variant="pencilGrayscale" width="lg" isCentered />} label={t('consultationForms.manageDropdown.edit')} onClick={handleEdit} showArrow={isMobile} />
      <DropdownItem isMobile={isMobile} icon={<Icon variant="documentCopy" width="lg" isCentered />} label={t('consultationForms.manageDropdown.duplicate')} onClick={handleDuplicate} />
      <DropdownItem
        isMobile={isMobile}
        icon={<Icon variant="bin" width="lg" isCentered />}
        label={t('consultationForms.manageDropdown.delete')}
        onClick={handleDeleteClick}
        isRedText={!isMobile}
        isRedCircle={isMobile}
      />
    </div>
  );

  return (
    <div ref={ref} data-bem="FormRowManageDropdown" className={cn({ [CN.dropdownContainer]: true, [CN.dark]: isDarkMode })}>
      <Button variant="outlinePrimaryMediumDark" onClick={handleToggleDropdown} width="small" tag="FormRowManageDropdown__trigger">
        <Icon variant="pencil" width="md" isCentered variantInvert />
      </Button>

      {isMobile ? (
        <MobileBottomSheet isOpen={isDropdownOpen} onClose={toggleDropdown}>
          {renderedContent}
        </MobileBottomSheet>
      ) : (
        isDropdownOpen && (
          <DropdownFoldout width={isDraft ? 'sm' : 'xs'} position={dropdownPosition} onClickOutside={toggleDropdown} refFromProps={ref}>
            <DropdownFoldout.Body>{renderedContent}</DropdownFoldout.Body>
          </DropdownFoldout>
        )
      )}

      <ConfirmDeleteFormModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        onDeactivate={handleDeactivateFromDeleteModal}
        formName={formName}
        isActive={isActive}
      />

      <DeactivateFormModal isOpen={isDeactivateOpen} onClose={closeDeactivate} onConfirm={handleDeactivateConfirm} formName={formName} />

      <ReactivateFormModal isOpen={isReactivateOpen} onClose={closeReactivate} onConfirm={handleReactivateConfirm} formName={formName} ruleTags={ruleTags} />
    </div>
  );
};
